import {
  pendingMarkdownDocumentInsertAtomFamily,
  pendingMarkdownNoteInsertAtomFamily,
} from '@/atoms/noteEditorAtoms';
import { stripCustomChatTags } from '@/components/chat/utils/stripCustomChatTags';
import { useSetAtom } from 'jotai';
import { useCallback } from 'react';

interface UseMarkdownNativeInsertsArgs {
  /** Session whose note the force-apply signal targets ('' for sessionless chats). */
  sessionId: string;
  /** Document whose editor the force-apply signal targets ('' when no document tab). */
  documentId: string;
  /** The chat answer, as markdown. */
  responseText: string;
}

// Appends the insert after the existing content, delimited by a horizontal
// rule; with no existing content, the insert stands alone.
const appendAfterRule = (existingContent: string, insertMarkdown: string): string => {
  const trimmed = existingContent.trimEnd();
  return trimmed ? `${trimmed}\n\n---\n\n${insertMarkdown}` : insertMarkdown;
};

/**
 * Content building for chat inserts into markdown-native editors.
 *
 * Every builder flattens custom chat tags (left in place, the markdown parser
 * treats them as html blocks and swallows the wrapped content) and — except
 * for new documents, which have no live editor — signals the matching
 * pending-insert atom so a focused editor applies the change instead of
 * swallowing it as a possible autosave echo.
 */
export const useMarkdownNativeInserts = ({
  sessionId,
  documentId,
  responseText,
}: UseMarkdownNativeInsertsArgs) => {
  const setPendingNoteInsert = useSetAtom(pendingMarkdownNoteInsertAtomFamily(sessionId));
  const setPendingDocumentInsert = useSetAtom(
    pendingMarkdownDocumentInsertAtomFamily(documentId)
  );

  const appendNoteMarkdown = useCallback(
    (existingNote: string) => {
      const consultNote = appendAfterRule(existingNote, stripCustomChatTags(responseText));
      setPendingNoteInsert(consultNote);
      return consultNote;
    },
    [responseText, setPendingNoteInsert]
  );

  const replaceNoteMarkdown = useCallback(() => {
    const consultNote = stripCustomChatTags(responseText);
    setPendingNoteInsert(consultNote);
    return consultNote;
  }, [responseText, setPendingNoteInsert]);

  const appendDocumentMarkdown = useCallback(
    (existingContent: string) => {
      const content = appendAfterRule(existingContent, stripCustomChatTags(responseText));
      setPendingDocumentInsert(content);
      return content;
    },
    [responseText, setPendingDocumentInsert]
  );

  const replaceDocumentMarkdown = useCallback(() => {
    const content = stripCustomChatTags(responseText);
    setPendingDocumentInsert(content);
    return content;
  }, [responseText, setPendingDocumentInsert]);

  const newDocumentMarkdown = useCallback(
    () => stripCustomChatTags(responseText),
    [responseText]
  );

  return {
    appendNoteMarkdown,
    replaceNoteMarkdown,
    appendDocumentMarkdown,
    replaceDocumentMarkdown,
    newDocumentMarkdown,
  };
};
